import { createSHA256 } from 'hash-wasm';

/**
 * Threshold for switching to streaming hash (8MB).
 *
 * Below this, hashing buffers the whole file into memory. The bulk uploader
 * hashes up to HASH_CONCURRENCY files at once, so a high threshold meant peak
 * memory of concurrency x threshold (e.g. 6 x 50MB = 300MB). Keep the threshold
 * small so all but genuinely tiny files take the constant-memory streaming path
 * (P5). The streaming path produces an identical SHA-256.
 */
const STREAMING_THRESHOLD = 8 * 1024 * 1024;

/**
 * Chunk size for streaming hash (2MB)
 * Balances memory usage vs. overhead
 */
const HASH_CHUNK_SIZE = 2 * 1024 * 1024;

/**
 * Compute SHA-256 hash of a file using in-memory approach
 * Fast for small files, but uses memory equal to file size
 */
async function computeFileHashInMemory(file: File): Promise<string> {
  const arrayBuffer = await file.arrayBuffer();
  const hashBuffer = await crypto.subtle.digest('SHA-256', arrayBuffer);
  const hashArray = Array.from(new Uint8Array(hashBuffer));
  const hashHex = hashArray.map(b => b.toString(16).padStart(2, '0')).join('');
  return hashHex;
}

/**
 * Compute SHA-256 hash of a file using streaming approach
 * Uses constant memory (~2MB) regardless of file size
 * Supports progress callback for UI feedback
 */
async function computeFileHashStreaming(
  file: File,
  onProgress?: (bytesProcessed: number, totalBytes: number) => void
): Promise<string> {
  const hasher = await createSHA256();
  hasher.init();

  let offset = 0;
  while (offset < file.size) {
    const end = Math.min(offset + HASH_CHUNK_SIZE, file.size);
    const chunk = file.slice(offset, end);
    const arrayBuffer = await chunk.arrayBuffer();
    hasher.update(new Uint8Array(arrayBuffer));

    offset = end;
    if (onProgress) {
      onProgress(offset, file.size);
    }
  }

  return hasher.digest('hex');
}

/**
 * Compute SHA-256 hash of a file
 * Automatically chooses in-memory or streaming based on file size
 * @param file - The file to hash
 * @param onProgress - Optional callback for progress updates (only called for large files)
 * @returns The SHA-256 hash as a hex string
 */
export async function computeFileHash(
  file: File,
  onProgress?: (bytesProcessed: number, totalBytes: number) => void
): Promise<string> {
  if (file.size > STREAMING_THRESHOLD) {
    return computeFileHashStreaming(file, onProgress);
  }
  return computeFileHashInMemory(file);
}

/**
 * Format file size for display
 */
export function formatFileSize(bytes: number): string {
  if (bytes === 0) return '0 B';
  const k = 1024;
  const sizes = ['B', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return parseFloat((bytes / Math.pow(k, i)).toFixed(1)) + ' ' + sizes[i];
}

/**
 * Validate file type based on extension
 */
export const ALLOWED_EXTENSIONS = [
  '.pdf',
  '.jpg', '.jpeg', '.png',
  '.doc', '.docx',
  '.xls', '.xlsx', '.xlsm',
  '.csv',
  '.json',
  '.txt',
  '.zip',
  '.gz', '.tar', '.tgz',
  '.7z',
  '.rar',
  '.bz2',
  '.xz',
  '.zst',
];
export const MAX_FILE_SIZE = 5 * 1024 * 1024 * 1024; // 5 GB per file — S3's hard limit for a single PUT (uploads are single-PUT, not multipart). Keep in sync with lambda/shared/s3-upload.ts.

export function validateFileType(fileName: string): boolean {
  const extension = fileName.toLowerCase().match(/\.[^.]+$/)?.[0];
  if (!extension) {
    return false;
  }
  return ALLOWED_EXTENSIONS.includes(extension);
}

export function validateFileSize(fileSize: number): boolean {
  return fileSize > 0 && fileSize <= MAX_FILE_SIZE;
}

export function getFileValidationError(file: File): string | null {
  if (!validateFileType(file.name)) {
    return `Only ${ALLOWED_EXTENSIONS.join(', ')} files are allowed`;
  }
  if (!validateFileSize(file.size)) {
    return `File size must be less than ${MAX_FILE_SIZE / 1024 / 1024 / 1024} GB per file`;
  }
  return null;
}
